/**
 * The personalizer's last step, "Završi" (docs/product-page-plan-20261031.md
 * §4): when the gift is needed, what the delivery promise is, how many. The
 * popup shows it as its last screen; the inline form as its last section.
 *
 * It owns no order data of its own. The date lives in the connector's page
 * field (`personalizy_requested_delivery`, kept in the cart form) and the
 * quantity in WooCommerce's own input, so the classic form, the AJAX add and
 * the server check all read the same two values.
 */

import { dealStep, nextDealStep, percentText, type DealTier } from './price-summary.js';
import {
  DELIVERY_TEXTS, WEEK_HEAD, dayGenitive, deliveryError, etaView, fetchEta, firstSelectable, monthGrid, monthTitle,
  readDeliveryConfig, type DeliveryConfig, type EtaData,
} from './delivery.js';

/** The most one add takes (the connector's own limit, `MAX_QUANTITY`): more would be cut silently. */
export const MAX_PIECES = 50;

export const FINISH_TEXTS = {
  title: 'Završi',
  quantity: 'Količina',
  less: 'Manje',
  more: 'Više',
  addToCart: 'Dodaj u korpu',
  pieces: (n: number): string => `${n} kom`,
  dealOn: (percent: number, n: number): string => `Popust za ${n} kom.: −${percentText(percent)} na celu cenu.`,
  dealNext: (more: number, percent: number): string => `Dodaj još ${more} kom. i popust je −${percentText(percent)}.`,
  dealCoupon: 'Ne sabira se sa kuponom.',
  same: (n: number): string => `Isti dizajn na svih ${n}`,
  each: 'Svaki komad drugačiji',
  eachNote: 'Dodaješ jedan po jedan: posle svakog promeni sliku ili tekst, ostalo ostaje.',
  piece: (k: number, n: number): string => `Komad ${k} od ${n}`,
  pieceNote: 'Svaki komad je posebna stavka u korpi.',
  stop: 'Prekini',
};

/** A run of different pieces: how many were planned, how many are in the cart. */
export interface PieceRun {
  total: number;
  done: number;
}

/** The page's delivery field (connector `class-personalizy-delivery.php`), as `delivery.js` left it. */
interface PageDelivery {
  set?: (value: string) => void;
}

export interface FinishPanelOptions {
  /** The cart form the date and quantity are posted with. */
  form: HTMLFormElement | null;
  /** `window.personalizyDeliveryConfig`, raw. */
  deliveryConfig: unknown;
  /** The product or the picked variation the promise is for. */
  productId: () => number;
  now?: () => Date;
  fetch?: (input: string, init: RequestInit) => Promise<Response>;
  /** The quantity changed (prices are redrawn). */
  onQuantity?: () => void;
  /** The product's pieces deal: its steps become quick picks ("3 kom −10%"). */
  deal?: { label: string; tiers: DealTier[] };
  /** Several pieces may each get their own design (not while editing a cart line). */
  allowPieces?: boolean;
  /** "Isti dizajn" / "Svaki komad drugačiji" changed. */
  onPieceMode?: () => void;
  /** "Prekini" in a run of different pieces. */
  onStopRun?: () => void;
}

export interface FinishPanel {
  element: HTMLElement;
  /** Fetches a fresh promise (the step opened, the variation changed). */
  refresh(): void;
  /** Why the add cannot go on ('' when it can); shows it too. */
  validate(): string;
  /** The page has a date question at all. */
  readonly hasDate: boolean;
  /** One line for the page face: "Stiže 3. ili 4. novembra ako poručiš danas", or ''. */
  headline(): Promise<string>;
  /** Redraws the deal picks and line (the quantity changed elsewhere). */
  refreshDeal(): void;
  /** "Svaki komad drugačiji" is chosen (and more than one piece). */
  pieceMode(): boolean;
  /** Shows a run of different pieces ("Komad 2 od 3") in place of the quantity, or ends it. */
  setRun(run: PieceRun | undefined): void;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const chevron = (dir: 'l' | 'r'): string =>
  `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${dir === 'l' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'}"/></svg>`;

export function createFinishPanel(options: FinishPanelOptions): FinishPanel {
  const now = options.now ?? (() => new Date());
  const config: DeliveryConfig | undefined = readDeliveryConfig(options.deliveryConfig);
  const form = options.form;
  const dateInput = (): HTMLInputElement | null => form?.querySelector<HTMLInputElement>('[name="personalizy_requested_delivery"]') ?? null;
  const pageField = (): (HTMLElement & { personalizyDelivery?: PageDelivery }) | null =>
    document.querySelector<HTMLElement & { personalizyDelivery?: PageDelivery }>('[data-personalizy-delivery]');
  const qtyInput = (): HTMLInputElement | null => form?.querySelector<HTMLInputElement>('input[name="quantity"]') ?? null;
  const hasDate = !!config && !!dateInput();

  const root = el('div', 'pz-finish');

  // ── When ───────────────────────────────────────────────────────────────
  const when = el('section', 'pz-finish-when');
  const pageLabel = pageField()?.querySelector('label')?.textContent?.replace(/\*/g, '').trim();
  const pageHelp = pageField()?.querySelector('#personalizy-delivery-help')?.textContent?.trim();
  const heading = el('h4', 'pz-finish-label', pageLabel || DELIVERY_TEXTS.label);
  if (config?.required) heading.append(el('span', 'pz-req', ' *'));
  const choice = el('div', 'pz-finish-choice');
  choice.setAttribute('role', 'group');
  choice.setAttribute('aria-label', pageLabel || DELIVERY_TEXTS.label);
  const asapBtn = el('button', 'pz-finish-opt');
  asapBtn.type = 'button';
  const asapText = el('span', 'pz-finish-opt-title', DELIVERY_TEXTS.asap);
  // The note under "Što pre" is the first thing a small screen drops (the promise below says it).
  const asapSub = el('span', 'pz-finish-opt-sub is-note', DELIVERY_TEXTS.asapNote);
  asapBtn.append(asapText, asapSub);
  const pickBtn = el('button', 'pz-finish-opt');
  pickBtn.type = 'button';
  const pickText = el('span', 'pz-finish-opt-title', DELIVERY_TEXTS.pick);
  const pickSub = el('span', 'pz-finish-opt-sub', '');
  pickBtn.append(pickText, pickSub);
  choice.append(asapBtn, pickBtn);

  const calendar = el('div', 'pz-dcal');
  calendar.hidden = true;
  const calHead = el('div', 'pz-dcal-head');
  const prev = el('button', 'pz-dcal-nav');
  prev.type = 'button';
  prev.innerHTML = chevron('l');
  prev.setAttribute('aria-label', DELIVERY_TEXTS.prevMonth);
  const calTitle = el('strong', 'pz-dcal-title');
  calTitle.setAttribute('aria-live', 'polite');
  const next = el('button', 'pz-dcal-nav');
  next.type = 'button';
  next.innerHTML = chevron('r');
  next.setAttribute('aria-label', DELIVERY_TEXTS.nextMonth);
  calHead.append(prev, calTitle, next);
  const grid = el('div', 'pz-dcal-grid');
  grid.setAttribute('role', 'grid');
  calendar.append(calHead, grid);

  const help = el('p', 'pz-finish-help', pageHelp ?? '');
  help.hidden = !pageHelp;
  const error = el('p', 'pz-finish-error');
  error.setAttribute('role', 'alert');
  error.hidden = true;
  when.append(heading, choice, calendar, help, error);

  // ── The promise ────────────────────────────────────────────────────────
  const eta = el('div', 'pz-eta');
  eta.hidden = true;
  eta.setAttribute('aria-live', 'polite');

  // ── How many ───────────────────────────────────────────────────────────
  const qtyRow = el('section', 'pz-finish-qty');
  const qtyLabel = el('span', 'pz-finish-label', FINISH_TEXTS.quantity);
  const stepper = el('div', 'pz-qty');
  const minus = el('button', 'pz-qty-btn', '−');
  minus.type = 'button';
  minus.setAttribute('aria-label', FINISH_TEXTS.less);
  const count = el('output', 'pz-qty-val');
  const plus = el('button', 'pz-qty-btn', '+');
  plus.type = 'button';
  plus.setAttribute('aria-label', FINISH_TEXTS.more);
  stepper.append(minus, count, plus);
  qtyRow.append(qtyLabel, stepper);
  qtyRow.hidden = !qtyInput() || qtyInput()!.type === 'hidden';
  // The deal's steps as one-tap picks above the stepper, and what the
  // quantity earns or would earn with one more.
  const tiers = (options.deal?.tiers ?? []).filter((t) => t.min >= 2 && t.percent > 0).sort((a, b) => a.min - b.min);
  const picks = el('div', 'pz-deal-picks');
  picks.setAttribute('role', 'group');
  picks.setAttribute('aria-label', options.deal?.label ?? FINISH_TEXTS.quantity);
  const dealLine = el('p', 'pz-deal-line');
  dealLine.setAttribute('aria-live', 'polite');
  const qtyBox = el('section', 'pz-finish-qtybox');
  // Several pieces: the same design on all, or each its own.
  const pieceChoice = el('div', 'pz-piece-choice');
  pieceChoice.setAttribute('role', 'group');
  const sameBtn = el('button', 'pz-piece-opt');
  sameBtn.type = 'button';
  const eachBtn = el('button', 'pz-piece-opt', FINISH_TEXTS.each);
  eachBtn.type = 'button';
  const eachNote = el('p', 'pz-piece-note', FINISH_TEXTS.eachNote);
  pieceChoice.append(sameBtn, eachBtn);
  const runBox = el('div', 'pz-piece-run');
  runBox.hidden = true;
  const runText = el('strong', 'pz-piece-run-text');
  const runStop = el('button', 'pz-piece-stop', FINISH_TEXTS.stop);
  runStop.type = 'button';
  runBox.append(runText, el('span', 'pz-piece-run-note', FINISH_TEXTS.pieceNote), runStop);
  let each = false;
  let run: PieceRun | undefined;
  if (tiers.length > 0 && !qtyRow.hidden) {
    for (const n of [1, ...tiers.map((t) => t.min)]) {
      const tier = tiers.find((t) => t.min === n);
      const pick = el('button', 'pz-deal-pick');
      pick.type = 'button';
      pick.dataset.n = String(n);
      pick.append(el('span', 'pz-deal-n', FINISH_TEXTS.pieces(n)));
      if (tier) pick.append(el('span', 'pz-deal-off', `−${percentText(tier.percent)}`));
      picks.append(pick);
    }
    qtyBox.append(picks, qtyRow, dealLine);
  } else {
    qtyBox.append(qtyRow);
  }
  if (options.allowPieces && !qtyRow.hidden) qtyBox.append(pieceChoice, eachNote);
  qtyBox.append(runBox);

  if (hasDate) root.append(when, eta);
  else root.append(eta);
  root.append(qtyBox);

  // ── State ──────────────────────────────────────────────────────────────
  // Nothing chosen yet only where a date is required: otherwise "Što pre" is the answer.
  let mode: 'none' | 'asap' | 'date' = config?.required ? 'none' : 'asap';
  let shown = { y: 0, m: 0 };
  const value = (): string => dateInput()?.value ?? '';

  const write = (iso: string): void => {
    const api = pageField()?.personalizyDelivery;
    if (api?.set) api.set(iso);
    const input = dateInput();
    if (input && input.value !== iso) {
      input.value = iso;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const drawChoice = (): void => {
    const picked = value();
    asapBtn.setAttribute('aria-pressed', String(mode === 'asap'));
    // The help ("Obeleži datum…") speaks to picking a date; a small screen shows it only then.
    root.classList.toggle('is-date', mode === 'date');
    pickBtn.setAttribute('aria-pressed', String(mode === 'date'));
    pickText.textContent = picked && mode === 'date' ? dayGenitive(picked) : DELIVERY_TEXTS.pick;
    pickSub.textContent = picked && mode === 'date' ? DELIVERY_TEXTS.change : '';
    pickSub.hidden = !pickSub.textContent;
  };

  const drawMonth = (): void => {
    if (!config) return;
    calTitle.textContent = monthTitle(shown.y, shown.m);
    grid.textContent = '';
    const head = el('div', 'pz-dcal-row pz-cal-week');
    for (const name of WEEK_HEAD) head.append(el('span', 'pz-dcal-wd', name));
    grid.append(head);
    const picked = value();
    let any = false;
    for (const week of monthGrid(shown.y, shown.m, config, now())) {
      const row = el('div', 'pz-dcal-row');
      row.setAttribute('role', 'row');
      for (const day of week) {
        const cell = el('button', 'pz-dcal-day', String(day.day));
        cell.type = 'button';
        cell.setAttribute('role', 'gridcell');
        cell.dataset.day = day.iso;
        cell.disabled = !day.selectable || !day.inMonth;
        cell.classList.toggle('is-out', !day.inMonth);
        cell.classList.toggle('is-today', day.today);
        cell.classList.toggle('is-picked', day.iso === picked);
        cell.setAttribute('aria-label', dayGenitive(day.iso));
        if (day.iso === picked) cell.setAttribute('aria-selected', 'true');
        if (day.selectable && day.inMonth) any = true;
        row.append(cell);
      }
      grid.append(row);
    }
    // Paging stops where nothing could be picked any more.
    const first = firstSelectable(config, now());
    const firstMonth = first ? Number(first.slice(0, 4)) * 12 + Number(first.slice(5, 7)) : 0;
    const here = shown.y * 12 + shown.m;
    prev.disabled = !first || here <= firstMonth;
    next.disabled = !any && here > firstMonth;
  };

  const showMonthOf = (iso: string): void => {
    shown = { y: Number(iso.slice(0, 4)), m: Number(iso.slice(5, 7)) };
    drawMonth();
  };

  const say = (message: string): void => {
    error.textContent = message;
    error.hidden = !message;
    root.classList.toggle('has-error', !!message);
  };

  // The promise, always fresh: an answer that arrives after a newer question is dropped.
  let standard: EtaData | undefined;
  let revision = 0;
  const refresh = (): void => {
    if (!config?.eta) return;
    const mine = ++revision;
    const picked = mode === 'date' ? value() : '';
    const product = options.productId();
    void (async () => {
      const base = await fetchEta(config, product, '', options.fetch);
      if (mine !== revision) return;
      standard = base;
      const data = picked ? await fetchEta(config, product, picked, options.fetch) : base;
      if (mine !== revision) return;
      const view = etaView(data, { rules: config, now: now(), ...(standard ? { standard } : {}) });
      drawEta(view);
    })();
  };

  const drawEta = (view: ReturnType<typeof etaView>): void => {
    eta.textContent = '';
    eta.hidden = !view;
    if (!view) return;
    // The ETA plugin's timeline, drawn the same way: the headline, then
    // Poručeno → Poslato → Stiže in three circles joined by a line.
    eta.className = `pz-eta is-${view.tone}`;
    const head = el('p', 'pz-eta-head', view.header);
    const steps = el('ol', 'pz-eta-steps');
    for (const step of view.steps) {
      const li = el('li', `pz-eta-step${step.state ? ` is-${step.state}` : ''}`);
      const day = el('span', 'pz-eta-day');
      if (step.struck) {
        day.append(el('del', '', step.struck), document.createTextNode(' → '));
      }
      day.append(document.createTextNode(step.day));
      li.append(el('span', 'pz-eta-icon', step.icon), day, el('span', 'pz-eta-status', step.status));
      steps.append(li);
    }
    eta.append(head, steps);
    if (view.footer) eta.append(el('p', 'pz-eta-foot', view.footer));
    if (view.suggest) {
      const take = el('button', 'pz-btn-ghost pz-eta-take', DELIVERY_TEXTS.pickSafe(dayGenitive(view.suggest)));
      take.type = 'button';
      take.addEventListener('click', () => {
        mode = 'date';
        write(view.suggest!);
        say('');
        showMonthOf(view.suggest!);
        drawChoice();
        refresh();
      });
      eta.append(take);
    }
  };

  // ── Events ─────────────────────────────────────────────────────────────
  asapBtn.addEventListener('click', () => {
    mode = 'asap';
    calendar.hidden = true;
    // A required date has no "whenever": the first day that can be picked.
    const first = config?.required ? firstSelectable(config, now()) : undefined;
    write(first ?? '');
    say('');
    drawChoice();
    refresh();
  });
  pickBtn.addEventListener('click', () => {
    mode = 'date';
    calendar.hidden = false;
    const at = value() || (config ? firstSelectable(config, now()) : undefined);
    if (at) showMonthOf(at);
    drawChoice();
    (grid.querySelector<HTMLButtonElement>('.is-picked') ?? grid.querySelector<HTMLButtonElement>('.pz-dcal-day:not(:disabled)'))?.focus?.();
  });
  grid.addEventListener('click', (event) => {
    const cell = (event.target as HTMLElement).closest<HTMLButtonElement>('.pz-dcal-day');
    if (!cell || cell.disabled || !cell.dataset.day) return;
    write(cell.dataset.day);
    say('');
    drawMonth();
    drawChoice();
    calendar.hidden = true;
    refresh();
  });
  prev.addEventListener('click', () => {
    shown = shown.m === 1 ? { y: shown.y - 1, m: 12 } : { y: shown.y, m: shown.m - 1 };
    drawMonth();
  });
  next.addEventListener('click', () => {
    shown = shown.m === 12 ? { y: shown.y + 1, m: 1 } : { y: shown.y, m: shown.m + 1 };
    drawMonth();
  });

  const drawQty = (): void => {
    const input = qtyInput();
    const n = Math.max(1, Number(input?.value) || 1);
    count.textContent = String(n);
    const min = Number(input?.min) || 1;
    const max = Math.min(Number(input?.max) || MAX_PIECES, MAX_PIECES);
    minus.disabled = n <= min;
    plus.disabled = n >= max;
    // Same design or each its own: only with more than one piece, never during a run.
    const several = n > 1 && !run && !!options.allowPieces;
    pieceChoice.hidden = !several;
    eachNote.hidden = !several || !each;
    sameBtn.textContent = FINISH_TEXTS.same(n);
    sameBtn.setAttribute('aria-pressed', String(!each));
    eachBtn.setAttribute('aria-pressed', String(each));
    if (run) {
      runBox.hidden = false;
      runText.textContent = FINISH_TEXTS.piece(run.done + 1, run.total);
      picks.hidden = true;
      qtyRow.hidden = true;
    } else {
      runBox.hidden = true;
      picks.hidden = tiers.length === 0;
      qtyRow.hidden = !qtyInput() || qtyInput()!.type === 'hidden';
    }
    if (tiers.length > 0) {
      // In a run the deal is the one the whole run reaches.
      const pieces = run ? run.total : n;
      const reached = dealStep(tiers, pieces);
      for (const pick of picks.querySelectorAll<HTMLButtonElement>('.pz-deal-pick')) {
        const m = Number(pick.dataset.n);
        // The pick of the step reached (5 picked for 6 pieces too).
        const on = reached ? m === reached.min : m === 1 && n === 1;
        pick.setAttribute('aria-pressed', String(on));
        pick.disabled = m > max;
      }
      const next = run ? undefined : nextDealStep(tiers, n);
      const parts: string[] = [];
      if (reached) parts.push(FINISH_TEXTS.dealOn(reached.percent, pieces));
      if (next) parts.push(FINISH_TEXTS.dealNext(next.min - n, next.percent));
      parts.push(FINISH_TEXTS.dealCoupon);
      dealLine.textContent = parts.join(' ');
      dealLine.classList.toggle('is-on', !!reached);
    }
  };
  const setQty = (n: number): void => {
    const input = qtyInput();
    if (!input) return;
    input.value = String(n);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const $ = (window as unknown as { jQuery?: (el: unknown) => { trigger(name: string): unknown } }).jQuery;
    try {
      $?.(input).trigger('change');
    } catch {
      /* a theme's own handler is not ours to fail */
    }
    drawQty();
    options.onQuantity?.();
  };
  picks.addEventListener('click', (event) => {
    const pick = (event.target as HTMLElement).closest<HTMLButtonElement>('.pz-deal-pick');
    if (pick && !pick.disabled) setQty(Number(pick.dataset.n) || 1);
  });
  sameBtn.addEventListener('click', () => {
    each = false;
    drawQty();
    options.onPieceMode?.();
  });
  eachBtn.addEventListener('click', () => {
    each = true;
    drawQty();
    options.onPieceMode?.();
  });
  runStop.addEventListener('click', () => options.onStopRun?.());
  minus.addEventListener('click', () => setQty(Math.max(1, (Number(qtyInput()?.value) || 1) - 1)));
  plus.addEventListener('click', () => {
    const input = qtyInput();
    const max = Math.min(Number(input?.max) || MAX_PIECES, MAX_PIECES);
    setQty(Math.min(max, (Number(input?.value) || 1) + 1));
  });
  qtyInput()?.addEventListener('change', drawQty);

  // A date restored from the cart (an edit) or the browser's back button.
  if (value() && config) {
    if (deliveryError(value(), config, now())) write('');
    else mode = 'date';
  }
  drawChoice();
  drawQty();

  return {
    element: root,
    hasDate,
    refresh: () => {
      drawQty();
      drawChoice();
      refresh();
    },
    validate: () => {
      if (!config || !hasDate) return '';
      const message = deliveryError(value(), config, now());
      say(message);
      if (message && mode === 'date') {
        calendar.hidden = false;
        const at = firstSelectable(config, now());
        if (at && !value()) showMonthOf(at);
      }
      return message;
    },
    refreshDeal: drawQty,
    pieceMode: () => each && !!options.allowPieces && Math.max(1, Number(qtyInput()?.value) || 1) > 1,
    setRun: (next) => {
      run = next ? { ...next } : undefined;
      if (!run) each = false;
      drawQty();
    },
    headline: async () => {
      if (!config?.eta) return '';
      const view = etaView(await fetchEta(config, options.productId(), '', options.fetch), { rules: config, now: now() });
      return view?.header.replace(/^\S+\s/, '') ?? '';
    },
  };
}
